import * as XLSX from 'xlsx'
import { CATEGORY_PRESETS } from './presets'
import { ExcelParseError, ExcelParser } from './ExcelParser'
import type {
  CategoryImportPreset,
  CategoryParseResult,
  CategoryTargetField,
  ParsedCategory
} from './types'

/**
 * Парсер выгрузки категорий.
 *
 * Источник задаёт иерархию значением в колонке «родитель»: верхний уровень
 * помечается специальным маркером (например, «Главный экран»). Маркер — это
 * настройка пресета (`rootMarkers`), а не понятие домена: в модели верхний
 * уровень = `parentName = null`.
 */
export class CategoryParser {
  constructor(private readonly presets: CategoryImportPreset[] = CATEGORY_PRESETS) {}

  /** Распарсить книгу из ArrayBuffer (например, File.arrayBuffer()). */
  parse(data: ArrayBuffer, forcedPresetId?: string): CategoryParseResult {
    const workbook = XLSX.read(data, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    if (!sheetName) {
      throw new ExcelParseError('Книга не содержит листов')
    }
    const sheet = workbook.Sheets[sheetName]
    const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
      defval: null
    })
    if (records.length === 0) {
      throw new ExcelParseError('Лист пуст — нет строк для импорта')
    }

    const headers = Object.keys(records[0] ?? {})
    const preset = forcedPresetId
      ? this.presets.find((p) => p.id === forcedPresetId)
      : this.detectPreset(headers)

    if (!preset) {
      throw new ExcelParseError(
        `Не удалось определить формат выгрузки категорий. Заголовки: ${
          headers.join(', ') || '(пусто)'
        }. Поддерживаются: ${this.presets.map((p) => p.label).join(', ')}.`
      )
    }

    const resolved = this.resolveColumns(headers, preset.mapping)
    if (!resolved.name) {
      throw new ExcelParseError(
        `Пресет «${preset.label}» не нашёл колонку с названием категории`
      )
    }

    const rows: ParsedCategory[] = []
    const warnings: string[] = []
    let skipped = 0

    for (const record of records) {
      const name = this.readString(record, resolved.name)
      if (!name) {
        skipped++
        continue
      }
      rows.push({
        name,
        parentName: this.normalizeParent(
          this.readString(record, resolved.parent),
          preset.rootMarkers
        ),
        department: this.readString(record, resolved.department) || undefined,
        isActive: this.readActive(record, resolved.isActive),
        // Порядок строк в выгрузке = порядок на главном экране.
        sortOrder: rows.length,
        imageUrl: this.readString(record, resolved.imageUrl) || undefined,
        raw: record
      })
    }

    this.validate(rows, warnings)
    return { presetId: preset.id, rows, skipped, warnings }
  }

  /**
   * Похожа ли книга на выгрузку категорий.
   * Нужно, чтобы UI импорта мог выбрать парсер до чтения строк.
   */
  static isCategoryFile(headers: string[]): boolean {
    const normalized = headers.map((h) => ExcelParser.normalizeHeader(h))
    const has = (aliases: string[]): boolean =>
      aliases.some((alias) => normalized.includes(ExcelParser.normalizeHeader(alias)))
    return (
      has(['название', 'наименование', 'name']) &&
      has(['категория', 'родитель', 'родительская категория', 'parent']) &&
      !has(['цена', 'price'])
    )
  }

  /** Авто-детект пресета по числу совпавших колонок. */
  private detectPreset(headers: string[]): CategoryImportPreset | undefined {
    const normalized = headers.map((h) => ExcelParser.normalizeHeader(h))
    let best: { preset: CategoryImportPreset; score: number } | undefined

    for (const preset of this.presets) {
      const resolved = this.resolveColumns(headers, preset.mapping, normalized)
      if (!resolved.name || resolved.parent === undefined) {
        continue
      }
      const score = (Object.keys(resolved) as CategoryTargetField[]).filter(
        (key) => resolved[key] !== undefined
      ).length
      if (!best || score > best.score) {
        best = { preset, score }
      }
    }
    return best?.preset
  }

  /** Сопоставить поля категории с фактическими заголовками книги. */
  private resolveColumns(
    headers: string[],
    mapping: CategoryImportPreset['mapping'],
    normalizedHeaders?: string[]
  ): Partial<Record<CategoryTargetField, string>> {
    const normalized =
      normalizedHeaders ?? headers.map((h) => ExcelParser.normalizeHeader(h))
    const result: Partial<Record<CategoryTargetField, string>> = {}

    for (const field of Object.keys(mapping) as CategoryTargetField[]) {
      const raw = mapping[field]
      if (!raw) continue
      const candidates = raw.map((c) => ExcelParser.normalizeHeader(c))
      const idx = normalized.findIndex((h) => candidates.includes(h))
      if (idx !== -1) {
        result[field] = headers[idx]
      }
    }
    return result
  }

  /** Пустой родитель или маркер корня → верхний уровень. */
  private normalizeParent(raw: string, rootMarkers: string[]): string | null {
    const value = raw.trim()
    if (!value) return null
    const normalized = ExcelParser.normalizeHeader(value)
    const isRoot = rootMarkers.some(
      (marker) => ExcelParser.normalizeHeader(marker) === normalized
    )
    return isRoot ? null : value
  }

  /** «Активно»: пусто/да/true/1 → true; нет/false/0 → false. */
  private readActive(
    record: Record<string, unknown>,
    column?: string
  ): boolean {
    const raw = this.readString(record, column).toLowerCase()
    if (!raw) return true
    return !['нет', 'no', 'false', '0', 'неактивно', 'неактивна'].includes(raw)
  }

  /**
   * Проверить дерево: дубликаты, самоссылки, потерянные родители, циклы.
   * Проблемные связи поднимаются на верхний уровень, чтобы касса не падала.
   */
  private validate(rows: ParsedCategory[], warnings: string[]): void {
    const names = new Set(rows.map((row) => row.name))
    const seen = new Set<string>()
    for (const row of rows) {
      if (seen.has(row.name)) {
        warnings.push(`Дубликат категории: «${row.name}»`)
      }
      seen.add(row.name)
    }

    const byName = new Map(rows.map((row) => [row.name, row]))
    for (const row of rows) {
      if (row.parentName == null) continue

      if (row.parentName === row.name) {
        warnings.push(
          `Категория «${row.name}» ссылается сама на себя — поднята на верхний уровень`
        )
        row.parentName = null
        continue
      }

      if (!names.has(row.parentName)) {
        warnings.push(
          `Родитель «${row.parentName}» для «${row.name}» не найден — категория поднята на верхний уровень`
        )
        row.parentName = null
        continue
      }

      const chain = new Set<string>([row.name])
      let current: string | null = row.parentName
      while (current != null) {
        if (chain.has(current)) {
          warnings.push(
            `Цикл в категориях вокруг «${row.name}» — связь с родителем удалена`
          )
          row.parentName = null
          break
        }
        chain.add(current)
        current = byName.get(current)?.parentName ?? null
      }
    }
  }

  private readString(
    record: Record<string, unknown>,
    column?: string
  ): string {
    if (!column) return ''
    const value = record[column]
    return value == null ? '' : String(value).trim()
  }
}
